import type { SupabaseClient } from '@supabase/supabase-js';

export interface ActiveJobTimerEntry {
  booking_id: string;
}

export function isWorkerOnBreak(entry: {
  break_start?: string | null;
  break_end?: string | null;
}): boolean {
  return Boolean(entry.break_start && !entry.break_end);
}

export async function fetchActiveJobTimers(
  supabase: SupabaseClient,
  resourceId: string
): Promise<ActiveJobTimerEntry[]> {
  const { data, error } = await supabase
    .from('job_time_entries')
    .select('booking_id')
    .eq('resource_id', resourceId)
    .is('stopped_at', null);

  if (error) throw error;
  return (data ?? []) as ActiveJobTimerEntry[];
}

export async function stopActiveJobTimers(
  supabase: SupabaseClient,
  resourceId: string,
  entries: ActiveJobTimerEntry[]
): Promise<void> {
  for (const { booking_id } of entries) {
    const { error } = await supabase.rpc('stop_job_timer', {
      p_booking_id: booking_id,
      p_resource_id: resourceId,
      p_method: 'manual',
    });
    if (error) throw error;
  }
}

export async function startWorkerBreak(
  supabase: SupabaseClient,
  payrollEntryId: string,
  resourceId: string
): Promise<void> {
  const now = new Date();
  const { error } = await supabase
    .from('payroll_page')
    .update({ break_start: now.toISOString(), updated_at: now.toISOString() })
    .eq('id', payrollEntryId)
    .eq('resource_id', resourceId);

  if (error) throw error;
}

export async function endWorkerBreak(
  supabase: SupabaseClient,
  payrollEntryId: string,
  resourceId: string
): Promise<void> {
  const now = new Date();
  const { error } = await supabase
    .from('payroll_page')
    .update({ break_end: now.toISOString(), updated_at: now.toISOString() })
    .eq('id', payrollEntryId)
    .eq('resource_id', resourceId);

  if (error) throw error;
}

export async function clockOutWorker(
  supabase: SupabaseClient,
  params: {
    payrollEntryId: string;
    resourceId: string;
    clockIn: string;
    breakStart?: string | null;
    breakEnd?: string | null;
  }
): Promise<void> {
  const now = new Date();

  if (params.breakStart && !params.breakEnd) {
    const { error: breakError } = await supabase
      .from('payroll_page')
      .update({ break_end: now.toISOString(), updated_at: now.toISOString() })
      .eq('id', params.payrollEntryId)
      .eq('resource_id', params.resourceId);
    if (breakError) throw breakError;
  }

  const { data: currentEntry, error: fetchError } = await supabase
    .from('payroll_page')
    .select('*, resource:resources(hourly_rate)')
    .eq('id', params.payrollEntryId)
    .single();

  if (fetchError || !currentEntry) {
    throw fetchError ?? new Error('Payroll entry not found');
  }

  let totalMs = now.getTime() - new Date(params.clockIn).getTime();

  const { data: breaks, error: breaksError } = await supabase
    .from('payroll_page')
    .select('break_start, break_end')
    .eq('id', params.payrollEntryId)
    .not('break_start', 'is', null)
    .not('break_end', 'is', null);

  if (breaksError) throw breaksError;

  if (breaks) {
    for (const breakPeriod of breaks as { break_start: string; break_end: string }[]) {
      totalMs -=
        new Date(breakPeriod.break_end).getTime() -
        new Date(breakPeriod.break_start).getTime();
    }
  }

  const totalMinutes = Math.round(totalMs / (1000 * 60));
  const hours = totalMinutes / 60;
  const hourlyRate = (currentEntry.resource as { hourly_rate?: number | null } | null)
    ?.hourly_rate;
  const totalEarnings = hours * (hourlyRate || 0);

  const { error: updateError } = await supabase
    .from('payroll_page')
    .update({
      clock_out: now.toISOString(),
      total_minutes: totalMinutes,
      total_hours: hours,
      total_earnings: totalEarnings,
      status: 'completed',
      updated_at: now.toISOString(),
    })
    .eq('id', params.payrollEntryId)
    .eq('resource_id', params.resourceId);

  if (updateError) throw updateError;
}
